// =============================================================================
// PRIMITIVE PALETTE - Draggable primitives list (HTML5 Drag & Drop for React Flow)
// =============================================================================

import {
  User,
  Building2,
  FileText,
  Package,
  ArrowRightLeft,
  GitBranch,
  Calendar,
  MapPin,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { PRIMITIVES, type Primitive, type PrimitiveType } from '../types';

const ICON_MAP: Record<string, LucideIcon> = {
  User,
  Building2,
  FileText,
  Package,
  ArrowRightLeft,
  GitBranch,
  Calendar,
  MapPin,
};

interface DraggablePrimitiveProps {
  primitive: Primitive;
  onDragStart: (event: React.DragEvent, primitiveType: PrimitiveType) => void;
}

function DraggablePrimitive({ primitive, onDragStart }: DraggablePrimitiveProps) {
  const Icon = ICON_MAP[primitive.icon] || User;

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, primitive.type)}
      className={cn(
        'flex items-center gap-3 rounded-lg border border-secondary-200 bg-white p-3 cursor-grab transition-all',
        'hover:border-primary-300 hover:shadow-md',
        'dark:border-secondary-700 dark:bg-secondary-800 dark:hover:border-primary-600',
        'active:cursor-grabbing active:opacity-50 active:shadow-lg'
      )}
    >
      <div
        className="flex h-10 w-10 items-center justify-center rounded-lg"
        style={{ backgroundColor: `${primitive.color}20` }}
      >
        <Icon className="h-5 w-5" style={{ color: primitive.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-secondary-900 dark:text-white">
          {primitive.name}
        </p>
        <p className="text-xs text-secondary-500 truncate">{primitive.description}</p>
      </div>
    </div>
  );
}

interface PrimitivePaletteProps {
  onDragStart: (event: React.DragEvent, primitiveType: PrimitiveType) => void;
}

export function PrimitivePalette({ onDragStart }: PrimitivePaletteProps) {
  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-secondary-200 dark:border-secondary-700">
        <h3 className="font-semibold text-secondary-900 dark:text-white">Primitives</h3>
        <p className="text-xs text-secondary-500 mt-1">
          Drag and drop to add to canvas
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {PRIMITIVES.map((primitive) => (
          <DraggablePrimitive
            key={primitive.id}
            primitive={primitive}
            onDragStart={onDragStart}
          />
        ))}
      </div>
    </div>
  );
}
